import {useEffect, useMemo, useState} from "react";
import {useInvoices} from "../../../hooks/useInvoices";
import {invoiceApi} from "../../../services/invoiceService";
import Loader from "../../components/shared/Loader";
import QueryErrorState from "../../components/shared/QueryErrorState";
import EmptyState from "../../components/shared/EmptyState";
import ModalViewFacture from "./modalViewFacture";
import TablePagination from "./TablePagination";
import {showToastModal} from "../../components/shared/ToastModalSwal";

const isProfessionalPayment = (invoice) => (
  ["PRO_FORMA", "PROVIDER_INVOICE"].includes(invoice.documentKind)
  || ["AWAITING_UPLOAD", "ANALYZING", "VALIDATED", "REJECTED"]
    .includes(invoice.providerInvoiceStatus)
);

const paymentStatus = (invoice) => {
  if (invoice.isPaid || invoice.payoutStatus === "PAID") {
    return {label: "Payée", className: "bg-[#E7F8F0] text-[#13805E]"};
  }
  if (invoice.providerInvoiceStatus === "VALIDATED" || invoice.payoutStatus === "PROCESSING") {
    return {label: "En cours", className: "bg-[#FFF5DC] text-[#A96700]"};
  }
  return {label: "Non payée", className: "bg-[#FFF0F1] text-[#D92F40]"};
};

const DocButtons = ({onView, onDownload, loading}) => (
  <div className="mt-2 flex flex-wrap gap-2">
    <button type="button" onClick={onView} disabled={loading} className="rounded-lg border border-primary bg-white px-3 py-1.5 text-xs font-semibold text-primary disabled:opacity-50">Voir</button>
    <button type="button" onClick={onDownload} disabled={loading} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Télécharger</button>
  </div>
);

const ProviderPaymentTable = () => {
  const [page, setPage] = useState(0);
  const {data, isLoading, isError, error, refetch} = useInvoices({page, size: 10});
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => () => {
    if (selectedFile?.url) URL.revokeObjectURL(selectedFile.url);
  }, [selectedFile]);

  const rows = useMemo(() => data?.rows || [], [data?.rows]);
  const hasProfessionalPayments = rows.some(isProfessionalPayment);
  const summary = useMemo(() => rows.reduce((result, invoice) => {
    const amount = Number(invoice.amount || 0);
    const status = paymentStatus(invoice).label;
    if (status === "Non payée") result.unpaid += amount;
    if (status === "En cours") result.pending += amount;
    if (status === "Payée") result.paid += amount;
    return result;
  }, {unpaid: 0, pending: 0, paid: 0}), [rows]);

  const documentBlob = async (invoice, provider = false) => {
    setLoadingId(invoice.id);
    setMessage("");
    try {
      const blob = provider
        ? await invoiceApi.getProviderFile(invoice.id)
        : await invoiceApi.getFile(invoice.id);
      return URL.createObjectURL(blob);
    } catch (requestError) {
      setMessage(requestError?.response?.data?.message || "Impossible de charger ce document.");
      return null;
    } finally {
      setLoadingId(null);
    }
  };

  const view = async (invoice, provider = false) => {
    const url = await documentBlob(invoice, provider);
    if (url) setSelectedFile({url, ref: provider ? `${invoice.ref}-finale` : invoice.ref});
  };

  const download = async (invoice, provider = false) => {
    const url = await documentBlob(invoice, provider);
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${provider ? `${invoice.ref}-finale` : invoice.ref}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const upload = async (invoice, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) {
      setMessage("La facture doit être un fichier PDF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage("La facture dépasse la taille maximale de 10 Mo.");
      return;
    }
    setUploadingId(invoice.id);
    setProgress(0);
    setMessage("");
    try {
      const result = await invoiceApi.uploadProviderInvoice(invoice.id, file, ({loaded, total}) => {
        setProgress(total ? Math.round((loaded / total) * 100) : 0);
      });
      if (!result.valid) {
        setMessage((result.validation?.errors || [result.message]).filter(Boolean).join(" "));
      } else {
        showToastModal({
          success: true,
          title: "Facture validée",
          message: "Votre facture a été importée. Son paiement est en cours.",
        });
      }
      await refetch();
    } catch (requestError) {
      setMessage(requestError?.response?.data?.message || "Impossible d’analyser cette facture.");
    } finally {
      setUploadingId(null);
      setProgress(0);
    }
  };

  if (isLoading) return <Loader className="relative" />;
  if (isError) return <QueryErrorState error={error} onRetry={refetch} />;

  const renderDocument = (invoice, provider = false) => {
    const professional = isProfessionalPayment(invoice);
    if (provider && !invoice.providerInvoiceTitle) {
      return (
        <label className={`relative inline-flex cursor-pointer flex-col rounded-lg border border-dashed border-[#FF6600] bg-[#FFF8F2] px-3 py-2 text-xs font-semibold text-[#D85600] ${uploadingId === invoice.id ? "pointer-events-none opacity-70" : ""}`}>
          <span>{uploadingId === invoice.id ? `Analyse en cours… ${progress}%` : "Importer ma facture"}</span>
          <small className="font-normal">PDF · 10 Mo maximum</small>
          <input type="file" accept="application/pdf,.pdf" className="sr-only" disabled={uploadingId === invoice.id} onChange={(event) => upload(invoice, event.target.files?.[0])} />
        </label>
      );
    }
    return (
      <div className="rounded-xl border border-[#D9E4F3] bg-[#F8FBFF] p-3 text-xs">
        <strong className="block text-[#19295C]">
          {provider ? "Facture finale" : professional ? "Facture exemple" : "Reçu de paiement"}
        </strong>
        <small className="text-[#8490A3]">{provider ? `${invoice.ref}-finale.pdf` : `${invoice.ref}.pdf`}</small>
        <DocButtons loading={loadingId === invoice.id} onView={() => view(invoice, provider)} onDownload={() => download(invoice, provider)} />
      </div>
    );
  };

  return (
    <>
      <ModalViewFacture openModal={!!selectedFile} toggleModal={() => setSelectedFile(null)} fileUrl={selectedFile?.url} refrence={selectedFile?.ref} />
      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
        <Summary label="À facturer" value={summary.unpaid} color="text-[#FF6600]" />
        <Summary label="Paiements en cours" value={summary.pending} color="text-[#A96700]" />
        <Summary label="Total payé" value={summary.paid} color="text-[#13805E]" />
      </div>
      <div className="w-full rounded-xl border-l-4 border-primary bg-[#EEF6FF] px-4 py-3 text-xs leading-5 text-[#29466F] sm:text-sm">
        <strong>Comment être payé ?</strong> Les particuliers reçoivent leur reçu. Les professionnels téléchargent la facture exemple puis déposent leur facture finale au format PDF.
      </div>
      {message && <div role="alert" className="w-full rounded-lg bg-red-50 px-4 py-3 text-xs font-medium text-red-700 sm:text-sm">{message}</div>}
      <div className="w-full rounded-2xl border border-[#E2E2E2] bg-white p-3 sm:p-5">
        {!rows.length ? <EmptyState className="min-h-[50vh]" /> : (
          <>
            <div className="hidden overflow-x-auto lg:block">
              <table className={`w-full ${hasProfessionalPayments ? "min-w-[900px]" : "min-w-[760px]"} border-separate border-spacing-y-3 text-xs`}>
                <thead><tr className="text-left text-[11px] uppercase text-[#6D7890]"><th className="px-3">Réf.</th><th className="px-3">Période / interventions</th><th className="px-3">Montant</th><th className="px-3">Document HelpClic</th>{hasProfessionalPayments && <th className="px-3">Facture finale</th>}<th className="px-3">Statut</th></tr></thead>
                <tbody>{rows.map((invoice) => {
                  const professional = isProfessionalPayment(invoice);
                  const status = paymentStatus(invoice);
                  return <tr key={invoice.id} className="rounded-xl bg-[#F8FAFD] align-middle"><td className="rounded-l-xl px-3 py-4 font-bold text-primary">{invoice.ref}</td><td className="px-3 py-4"><strong>{invoice.interventions?.length || 0} intervention(s) rémunérée(s)</strong><small className="block text-[#8490A3]">Créé le {new Date(invoice.createdAt).toLocaleDateString("fr-FR")}</small></td><td className="px-3 py-4 text-sm font-bold">{Number(invoice.amount || 0).toFixed(2)} €</td><td className="px-3 py-4">{renderDocument(invoice)}</td>{hasProfessionalPayments && <td className="px-3 py-4">{professional ? renderDocument(invoice, true) : <span aria-hidden="true">—</span>}</td>}<td className="rounded-r-xl px-3 py-4"><span className={`inline-flex rounded-full px-3 py-2 text-[11px] font-bold ${status.className}`}>{status.label}</span></td></tr>;
                })}</tbody>
              </table>
            </div>
            <div className="grid gap-4 lg:hidden">{rows.map((invoice) => {
              const professional = isProfessionalPayment(invoice);
              const status = paymentStatus(invoice);
              return <article key={invoice.id} className="rounded-2xl border border-[#E5EAF2] p-4"><div className="flex items-start justify-between gap-3"><div><strong className="text-base text-primary">{invoice.ref}</strong><small className="block text-xs text-[#8490A3]">{invoice.interventions?.length || 0} intervention(s)</small></div><span className={`rounded-full px-3 py-1.5 text-[11px] font-bold ${status.className}`}>{status.label}</span></div><div className="my-3 text-xl font-bold text-[#19295C]">{Number(invoice.amount || 0).toFixed(2)} €</div><div className="grid gap-3 sm:grid-cols-2"><div>{renderDocument(invoice)}</div>{professional && <div>{renderDocument(invoice, true)}</div>}</div></article>;
            })}</div>
            <TablePagination page={page} setPage={setPage} count={Math.max(1, Math.ceil((data?.totalItems || 0) / 10))} />
          </>
        )}
      </div>
    </>
  );
};

const Summary = ({label, value, color}) => <div className="rounded-2xl bg-white p-4 shadow-sm"><small className="text-xs text-[#728099]">{label}</small><strong className={`mt-2 block text-xl sm:text-2xl ${color}`}>{Number(value || 0).toFixed(2)} €</strong></div>;

export default ProviderPaymentTable;
